import React, { useState, useEffect } from 'react'
import { Select, DropdownMenuPosition, SelectOption } from '@duik/it'
import { useDispatch } from 'react-redux'
import SyncIcon from '@material-ui/icons/Sync'
import SyncProblemIcon from '@material-ui/icons/SyncProblem'
import MainContainer from '../UI/MainContainer'
import TableSection from '../UI/TableSection'
import TableHeader from '../UI/TableHeader'
import CreateNewProject from './CreateNewProject'
import EditProject from './EditProject'
import ProjectActionModal from './ProjectActionModal'
import SmConfirmModal from '../UI/SmConfirmModal'
import ProjectItem from './ProjectItem'
import { useCreateProjectModal, useEditProjectModal } from './Shared/hooks'
import { AnalyticEvent } from '../analytics/GoogleAnalytics'
import {
  getProjects,
  createProject,
  editProject,
  archiveUnarchiveProject,
  importProcoreProjects,
  syncProcoreProjects
} from './Store/actions'
import { setSnackBarCopy, toggleSnackBar } from '../UI/Store/actions'
import { ProjectInput, Project, ArchiveType, ProcoreFetchType } from './Types/types'
import { ApplicationState } from 'Store/intialState'
import TableTopSection from 'UI/TableTopSection'
import {
  AdditionalElementContainer,
  StyledDownloadIconButton,
  StyledSyncIconButton,
  StyledTableContainer
} from './Styles/projects'
import useShallowEqualSelector from 'hooks/global'
import SmBannerModal from 'UI/SmBannerModal'
import moment from 'moment'
import { getToken, editCompany } from 'Companies/Store/actions'
import ImportProjects from './ImportProjects'
import ProcoreSettings from './ProcoreSettings'

const Projects: React.FC = () => {
  const {
    projects,
    snackBarCopy,
    snackBarVisible,
    company,
    loading,
    error,
    procoreLoading,
    companyLoading,
    companyFeatures
  } = useShallowEqualSelector((state: ApplicationState) => ({
    projects: state.projects,
    snackBarCopy: state.ui.snackBarCopy,
    snackBarVisible: state.ui.snackBarVisible,
    company: state.companies.company,
    loading: state.projects.loading,
    error: state.projects.error,
    procoreLoading: state.projects.procoreLoading,
    companyLoading: state.companies.loading,
    companyFeatures: state.user.userData.companyFeatures
  }))

  const [searchValue, setSearchValue] = useState<string>('')
  const [searchedValue, setSearchedValue] = useState<string>('')
  const [searchFilter, setSearchFilter] = useState<boolean>(false)
  const [projectsList, setProjectsList] = useState<Project[]>(projects.projectList)
  const { createNewProjectModal, handleCreateProjectModal } = useCreateProjectModal()
  const { editProjectModal, handleEditProjectModal } = useEditProjectModal()
  const [projectArchiveModal, setProjectArchiveModal] = useState<boolean>(false)
  const [projectUnarchiveModal, setProjectUnarchiveModal] = useState<boolean>(false)
  const [currentProjectDetail, setCurrentProjectDetail] = useState<Project | undefined>()
  const [activeFilter, setActiveFilter] = useState<SelectOption>({ value: 'all', label: 'All' })
  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [importProjects, setImportProjects] = useState<boolean>(false)
  const [procoreSettings, setProcoreSettings] = useState<boolean>(false)
  const [procoreFetching, setProcoreFetching] = useState<ProcoreFetchType>()

  const dispatch = useDispatch()

  const validateProcoreCredentials = () => {
    if (!company.procore || !company.procore.clientId || !company.procore.clientSecret) {
      setProcoreSettings(true)

      return false
    }

    const { clientId, clientSecret, authToken, authTokenExpiry } = company.procore
    const tokenValid = moment().isBefore(moment(authTokenExpiry))

    if (!authToken || !tokenValid) {
      dispatch(getToken(clientId, clientSecret))
      setModalVisible(true)

      return false
    }
    setModalVisible(true)

    return true
  }

  const handleProjectsImport = () => {
    const procoreValidated = validateProcoreCredentials()
    setProcoreFetching('getProjects')

    if (procoreValidated && company?.procore?.authToken) {
      dispatch(importProcoreProjects(company.procore.authToken))
    }
  }

  const handleProjectsSync = () => {
    const procoreValidated = validateProcoreCredentials()
    setProcoreFetching('syncProjects')

    const procoreProjects = projects.projectList.filter(project => project.procoreProjectId)
    if (procoreValidated && company?.procore?.authToken && procoreProjects.length) {
      dispatch(syncProcoreProjects(procoreProjects, company.procore.authToken))
    }
  }

  const editProjectModalToggle = (_id?: string) => {
    AnalyticEvent('Projects', 'EditProjectButtonPress')
    if (_id) {
      const project = projects.projectList.find(project => project._id === _id)
      if (project) {
        setCurrentProjectDetail(project)
      }
    }
    handleEditProjectModal()
  }

  const archiveProjectModalToggle = (_id: string, archiveUnarchive: ArchiveType) => {
    AnalyticEvent('Projects', 'ArchiveUnarchiveButtonPress', archiveUnarchive)
    const project = projects.projectList.find(project => project._id === _id)
    setCurrentProjectDetail(project)
    if (archiveUnarchive === 'archive') {
      setProjectArchiveModal(!projectArchiveModal)
    } else if (archiveUnarchive === 'unarchive') {
      setProjectUnarchiveModal(!projectUnarchiveModal)
    }
  }

  const handleGetProjects = () => {
    setProjectsList(projects.projectList)
  }

  const handleCreateProject = (values: ProjectInput) => {
    AnalyticEvent('Projects', 'CreateProjectButtonPress')

    dispatch(setSnackBarCopy('create'))
    dispatch(createProject(values))
    projectActionModalToggle()
  }

  const handleEditProject = (_id: string, values: ProjectInput) => {
    AnalyticEvent('Projects', 'EditProjectSaveButtonPress')

    dispatch(setSnackBarCopy('edit'))
    dispatch(editProject(_id, values))
    projectActionModalToggle()
  }

  const handleArchiveUnarchiveProject = (_id: string, archiveUnarchive: ArchiveType) => {
    dispatch(setSnackBarCopy(archiveUnarchive))
    if (archiveUnarchive === 'archive') {
      AnalyticEvent('Projects', 'ArchiveProjectButtonPress')
      setProjectArchiveModal(!projectArchiveModal)
    } else {
      AnalyticEvent('Projects', 'UnarchiveProjectButtonPress')
      setProjectUnarchiveModal(!projectUnarchiveModal)
    }
    if (currentProjectDetail) {
      dispatch(archiveUnarchiveProject(_id, currentProjectDetail, archiveUnarchive))
    }
    projectActionModalToggle()
  }

  const projectActionModalToggle = () => {
    dispatch(toggleSnackBar(true))
    setTimeout(() => {
      dispatch(toggleSnackBar(false))
    }, 2500)
  }

  const filterProjects = (filterType: SelectOption) => {
    switch (filterType.value) {
      case 'all':
        AnalyticEvent('Projects', 'FilterAllButtonPress')
        handleGetProjects()
        setActiveFilter(filterType)
        break
      case 'active':
        AnalyticEvent('Projects', 'FilterActiveButtonPress')
        const activeList = projects.projectList.filter(project => project.active === true)
        setProjectsList(activeList)
        setActiveFilter(filterType)
        break
      case 'archived':
        AnalyticEvent('Projects', 'FilterArchivedButtonPress')
        const archiveList = projects.projectList.filter(project => project.active === false)
        setProjectsList(archiveList)
        setActiveFilter(filterType)
        break
      default:
        break
    }
  }

  const search = (e: React.FormEvent) => {
    AnalyticEvent('Projects', 'SearchProjectButtonPressed')
    let searchList: Project[] = []
    e.preventDefault()
    //eslint-disable-next-line
    projects.projectList.map(item => {
      if (
        item.projectName.toLowerCase().search(searchValue.toLowerCase()) !== -1 ||
        item.projectNumber.toLowerCase().search(searchValue.toLowerCase()) !== -1
      ) {
        searchList = searchList.concat(item)
      }
    })
    setSearchedValue(searchValue)
    setProjectsList(searchList)
    setSearchFilter(true)
  }

  const clearSearch = () => {
    AnalyticEvent('Projects', 'ClearSearchButtonPressed')
    setSearchValue('')
    setSearchedValue('')
    setSearchFilter(false)
    setProjectsList(projects.projectList)
  }

  useEffect(() => {
    if (projects.procoreProjects.length) {
      setModalVisible(false)
      setImportProjects(true)
    }
  }, [projects.procoreProjects])

  useEffect(() => {
    if (company?.procore?.authToken && procoreFetching) {
      const companyInput = {
        procore: { ...company.procore, authToken: company.procore?.authToken }
      }
      dispatch(editCompany(company._id, companyInput))
      if (procoreFetching === 'getProjects') {
        setModalVisible(true)
        dispatch(importProcoreProjects(company.procore.authToken))
      } else if (procoreFetching === 'syncProjects') {
        setModalVisible(true)
        const procoreProjects = projects.projectList.filter(project => project.procoreProjectId)
        dispatch(syncProcoreProjects(procoreProjects, company.procore.authToken))
      }
    }
  }, [company?.procore?.authToken])

  useEffect(() => {
    dispatch(getProjects(true))
  }, []) //eslint-disable-line

  useEffect(() => {
    if (company?.procore?.clientId && company.procore.clientSecret) {
      dispatch(getToken(company.procore.clientId, company.procore.clientSecret))
    }
  }, [company.procore?.clientId, company.procore?.clientSecret])

  useEffect(() => {
    if (projects.projectList && searchValue === '' && searchedValue === '') {
      setProjectsList(projects.projectList)
    }
  }, [projects.loading, projects.projectList, searchValue, searchedValue])

  const procoreProjectsCount = projects.projectList.filter(project => project.procoreProjectId)
    .length

  return (
    <MainContainer>
      <StyledTableContainer
        style={importProjects || procoreSettings ? { display: 'none' } : undefined}
        show={!importProjects || !procoreSettings}
      >
        <TableTopSection
          title="Projects"
          loading={projects.loading}
          searchString={
            !projects.loading ? (
              <span style={{ fontSize: '0.875rem', color: '#9EA0A5', padding: '0 15px' }}>
                {!searchFilter &&
                  projects.projectList &&
                  searchedValue.length === 0 &&
                  `${projects.projectList.length} Projects`}
                {!projects.projectList && `0 Projects`}
                {searchFilter &&
                  `${projectsList.length}  ${
                    projectsList.length > 1 ? 'Projects' : 'Project'
                  } found for '${searchedValue}'`}
              </span>
            ) : (
              undefined
            )
          }
          searchSubmit={e => search(e)}
          searchXButtonPress={clearSearch}
          searchValue={searchValue}
          setSearchValue={v => setSearchValue(v)}
          handlePlusIconClick={handleCreateProjectModal}
          additionalElement={
            <AdditionalElementContainer>
              <Select
                className="filterSelectRight"
                activeOption={{
                  value: activeFilter.value,
                  label: (
                    <span className="haulViewTitle">
                      View:<span className="haulViewType">{activeFilter.label}</span>
                    </span>
                  )
                }}
                options={[
                  { value: 'all', label: 'All' },
                  {
                    value: 'active',
                    label: 'Active'
                  },
                  {
                    value: 'archived',
                    label: 'Archived'
                  }
                ]}
                onOptionClick={e => filterProjects(e)}
                menuPosition={DropdownMenuPosition['bottom-right']}
              />
              {companyFeatures.includes('procore') && (
                <>
                  <StyledDownloadIconButton onClick={handleProjectsImport} />
                  <StyledSyncIconButton onClick={handleProjectsSync} />
                </>
              )}
            </AdditionalElementContainer>
          }
        />

        <TableSection>
          <thead>
            <tr>
              {['PROJECT', 'ADDRESS', 'TYPE', 'STATUS', ''].map((item, i) => (
                <TableHeader title={item} key={i} />
              ))}
            </tr>
          </thead>
          <tbody>
            {!projects.loading && projects.projectList && projectsList ? (
              // eslint-disable-next-line
              projectsList.map((project, index) => {
                if (project.officeAddress) {
                  return (
                    <ProjectItem
                      key={index}
                      project={project}
                      archiveProjectModalToggle={archiveProjectModalToggle}
                      editProjectModalToggle={editProjectModalToggle}
                      index={index}
                      companyId={company._id}
                    />
                  )
                }
              })
            ) : (
              <tr></tr>
            )}
          </tbody>
        </TableSection>
      </StyledTableContainer>
      {modalVisible && !procoreSettings && !importProjects && (
        <SmBannerModal
          heading={!loading && error ? 'Sync Projects - Error' : 'Sync Projects'}
          text={
            companyLoading || procoreLoading
              ? 'Connecting to Procore…'
              : !error
              ? procoreFetching === 'syncProjects'
                ? `Successfully synced ${procoreProjectsCount} project${
                    procoreProjectsCount > 1 ? 's' : ''
                  }`
                : 'Connecting to Procore…'
              : procoreFetching === 'syncProjects'
              ? 'Error syncing procore projects'
              : 'Error fetching procore projects'
          }
          icon={!loading && error ? <SyncProblemIcon /> : <SyncIcon />}
          linkText={procoreLoading ? 'Cancel' : undefined}
          onLinkClick={() => setModalVisible(false)}
          buttonText={!procoreLoading ? 'Ok' : undefined}
          onButtonClick={() => setModalVisible(false)}
        />
      )}
      {procoreSettings && <ProcoreSettings setProcoreSettings={setProcoreSettings} />}
      {importProjects && <ImportProjects setImportProjects={setImportProjects} />}
      {createNewProjectModal ? (
        <CreateNewProject
          createNewProjectModalToggle={handleCreateProjectModal}
          createProject={handleCreateProject}
        />
      ) : (
        <></>
      )}
      {editProjectModal && currentProjectDetail ? (
        <EditProject
          editProjectDetail={currentProjectDetail}
          editProject={handleEditProject}
          editProjectModalToggle={editProjectModalToggle}
        />
      ) : (
        <></>
      )}
      {snackBarVisible && <ProjectActionModal projectAction={snackBarCopy} />}
      {currentProjectDetail && (projectArchiveModal || projectUnarchiveModal) ? (
        <SmConfirmModal
          heading={projectArchiveModal ? 'Archive Project' : 'Unarchive Project'}
          text={`Are you sure you want to ${projectArchiveModal ? 'archive' : 'unarchive'} ${
            currentProjectDetail.projectName
          }`}
          onCloseClick={() =>
            archiveProjectModalToggle(
              currentProjectDetail._id,
              projectArchiveModal ? 'archive' : 'unarchive'
            )
          }
          onOKClick={() =>
            archiveUnarchiveProject(
              currentProjectDetail._id,
              currentProjectDetail,
              projectArchiveModal ? 'archive' : 'unarchive'
            )
          }
          confirmButtonType="primary"
        />
      ) : (
        <></>
      )}
    </MainContainer>
  )
}

export default Projects
